import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Download, 
  FileSpreadsheet,
  FileText,
  RefreshCw,
  Users,
  Layers,
  Calendar,
  Building2
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useClient } from "@/context/ClientContext";
import { devWarn } from "../utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TDSSummary = () => {
  const { selectedClient, clientId } = useClient();
  const [summaryByParty, setSummaryByParty] = useState([]);
  const [summaryBySection, setSummaryBySection] = useState([]);
  const [periods, setPeriods] = useState({ financial_years: [], quarters: [], months: [] });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("party");
  
  // Filters
  const [selectedFY, setSelectedFY] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [viewType, setViewType] = useState("all");

  const fetchPeriods = useCallback(async () => {
    try {
      const params = clientId ? { client_id: clientId } : {};
      const response = await axios.get(`${API}/periods`, { params });
      setPeriods(response.data);
    } catch (err) { devWarn("fetchPeriods (non-critical):", err); }
  }, [clientId]);

  const fetchSummary = useCallback(async () => {
    if (!clientId) {
      setSummaryByParty([]);
      setSummaryBySection([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { view_type: viewType, client_id: clientId };
      if (selectedFY && selectedFY !== "all") params.financial_year = selectedFY;
      if (selectedQuarter && selectedQuarter !== "all") params.quarter = selectedQuarter;
      if (selectedMonth && selectedMonth !== "all") params.month = selectedMonth;
      
      const [partyRes, sectionRes] = await Promise.all([
        axios.get(`${API}/summary`, { params: { ...params, group_by: 'party' } }),
        axios.get(`${API}/summary`, { params: { ...params, group_by: 'section' } })
      ]);
      setSummaryByParty(partyRes.data);
      setSummaryBySection(sectionRes.data);
    } catch (err) {
      toast.error("Failed to load summary");
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedFY, selectedQuarter, selectedMonth, viewType]);

  useEffect(() => {
    if (clientId) queueMicrotask(() => fetchPeriods());
  }, [clientId, fetchPeriods]);

  useEffect(() => {
    queueMicrotask(() => fetchSummary());
  }, [fetchSummary]);

  const handleExportExcel = async () => {
    if (!clientId) {
      toast.error("Please select a client first");
      return;
    }
    try {
      setExporting(true);
      const params = { client_id: clientId };
      if (selectedFY && selectedFY !== "all") params.financial_year = selectedFY;
      if (selectedQuarter && selectedQuarter !== "all") params.quarter = selectedQuarter;
      if (selectedMonth && selectedMonth !== "all") params.month = selectedMonth;
      
      const response = await axios.get(`${API}/export/excel`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const period = selectedFY || 'All';
      link.setAttribute('download', `TDS_Summary_${period}_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel file downloaded");
    } catch (err) {
      toast.error("Failed to export Excel");
      // Error handled silently
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!clientId) {
      toast.error("Please select a client first");
      return;
    }
    try {
      setExporting(true);
      const params = { client_id: clientId };
      if (selectedFY && selectedFY !== "all") params.financial_year = selectedFY;
      if (selectedQuarter && selectedQuarter !== "all") params.quarter = selectedQuarter;
      if (selectedMonth && selectedMonth !== "all") params.month = selectedMonth;
      
      const response = await axios.get(`${API}/export/pdf`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const period = selectedFY || 'All';
      link.setAttribute('download', `TDS_Summary_${period}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF file downloaded");
    } catch (err) {
      toast.error("Failed to export PDF");
      // Error handled silently
    } finally {
      setExporting(false);
    }
  };

  const handleExportProfessionalReport = async (reportType) => {
    if (!clientId) {
      toast.error("Please select a client first");
      return;
    }
    try {
      setExporting(true);
      const params = { 
        client_id: clientId,
        report_type: reportType
      };
      if (selectedFY && selectedFY !== "all") params.financial_year = selectedFY;
      if (selectedQuarter && selectedQuarter !== "all") params.quarter = selectedQuarter;
      if (selectedMonth && selectedMonth !== "all") params.month = selectedMonth;
      
      const response = await axios.get(`${API}/export/professional-report`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const clientName = selectedClient?.short_name || 'Report';
      const period = selectedFY || 'All';
      link.setAttribute('download', `TDS_Report_${clientName}_${reportType}_${period}_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report downloaded`);
    } catch (err) {
      toast.error("Failed to export report");
      // Error handled silently
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Calculate grand totals
  const grandTotals = summaryByParty.reduce((acc, item) => ({
    amount: acc.amount + (item.total_amount || 0),
    tds: acc.tds + (item.total_tds || 0),
    count: acc.count + (item.transaction_count || 0)
  }), { amount: 0, tds: 0, count: 0 });

  if (!selectedClient) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500" data-testid="no-client">
        <Building2 className="w-16 h-16 mb-4 text-slate-300" />
        <p className="text-lg font-medium">No Client Selected</p>
        <p className="text-sm mt-1">Please select a client from the sidebar to view TDS summary</p>
      </div>
    );
  }

  return (
    <div data-testid="tds-summary-page">
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title">TDS Summary</h1>
          <p className="page-subtitle">{selectedClient.name} - Consolidated TDS summary</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchSummary} data-testid="refresh-summary-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleExportExcel} disabled={exporting} data-testid="export-excel-btn">
            {exporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Export Excel
          </Button>
          <Button variant="outline" onClick={handleExportPDF} disabled={exporting} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <div className="border-l border-slate-300 mx-2"></div>
          <Button variant="default" className="bg-blue-600 hover:bg-blue-700" onClick={() => handleExportProfessionalReport('monthly')} disabled={exporting} data-testid="monthly-report-btn">
            <Download className="w-4 h-4 mr-2" />
            Monthly Report
          </Button>
          <Button variant="default" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleExportProfessionalReport('quarterly')} disabled={exporting} data-testid="quarterly-report-btn">
            <Download className="w-4 h-4 mr-2" />
            Quarterly Report
          </Button>
          <Button variant="default" className="bg-purple-600 hover:bg-purple-700" onClick={() => handleExportProfessionalReport('annual')} disabled={exporting} data-testid="annual-report-btn">
            <Download className="w-4 h-4 mr-2" />
            Annual Report
          </Button>
        </div>
      </div>

      {/* Period Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600">Filter by:</span>
            </div>
            <Select value={selectedFY} onValueChange={setSelectedFY}>
              <SelectTrigger className="w-36" data-testid="summary-fy-filter">
                <SelectValue placeholder="Financial Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {periods.financial_years.map((fy) => (
                  <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
              <SelectTrigger className="w-36" data-testid="summary-quarter-filter">
                <SelectValue placeholder="Quarter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Quarters</SelectItem>
                <SelectItem value="Q1">Q1 (Apr-Jun)</SelectItem>
                <SelectItem value="Q2">Q2 (Jul-Sep)</SelectItem>
                <SelectItem value="Q3">Q3 (Oct-Dec)</SelectItem>
                <SelectItem value="Q4">Q4 (Jan-Mar)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-40" data-testid="summary-month-filter">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {periods.months.map((month) => (
                  <SelectItem key={month} value={month}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Grand Totals */}
      {summaryByParty.length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-slate-900 text-white">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Total Parties</p>
            <p className="text-2xl font-mono font-semibold">{summaryByParty.length}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Transactions</p>
            <p className="text-2xl font-mono font-semibold text-slate-900">{grandTotals.count}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Amount</p>
            <p className="text-2xl font-mono font-semibold text-slate-900">{formatCurrency(grandTotals.amount)}</p>
          </Card>
          <Card className="p-4 bg-emerald-50 border-emerald-200">
            <p className="text-xs text-emerald-600 uppercase tracking-wide">Total TDS</p>
            <p className="text-2xl font-mono font-semibold text-emerald-700">{formatCurrency(grandTotals.tds)}</p>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="party" data-testid="tab-party">
            <Users className="w-4 h-4 mr-2" />
            By Party
          </TabsTrigger>
          <TabsTrigger value="section" data-testid="tab-section">
            <Layers className="w-4 h-4 mr-2" />
            By Section
          </TabsTrigger>
        </TabsList>

        {/* Summary by Party */}
        <TabsContent value="party">
          <Card className="data-card" data-testid="summary-party-table">
            <CardHeader className="data-card-header">
              <CardTitle className="data-card-title">Summary by Party ({summaryByParty.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
              ) : summaryByParty.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="tds-table">
                    <thead>
                      <tr>
                        <th>Party Name</th>
                        <th>PAN</th>
                        <th>Section (1961)</th>
                        <th>Section (2025)</th>
                        <th className="text-right">Total Amount</th>
                        <th className="text-right">Total TDS</th>
                        <th className="text-right">Effective Rate</th>
                        <th className="text-right">Txn Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryByParty.map((item) => (
                        <tr key={`${item.party_name}-${item.pan}-${item.section}`} data-testid={`summary-party-row-${item.party_name}`}>
                          <td className="font-medium">{item.party_name}</td>
                          <td className="pan-cell">{item.pan || '-'}</td>
                          <td>{item.section ? <span className="section-badge">{item.section}</span> : <span className="text-slate-400">-</span>}</td>
                          <td className="text-xs text-slate-600">{item.new_section || '-'}</td>
                          <td className="amount-cell">{formatCurrency(item.total_amount)}</td>
                          <td className="amount-cell text-emerald-600">{formatCurrency(item.total_tds)}</td>
                          <td className="amount-cell">{item.effective_rate?.toFixed(2) || '0.00'}%</td>
                          <td className="amount-cell">{item.transaction_count}</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-100 font-semibold">
                        <td colSpan={4}>GRAND TOTAL</td>
                        <td className="amount-cell">{formatCurrency(grandTotals.amount)}</td>
                        <td className="amount-cell text-emerald-600">{formatCurrency(grandTotals.tds)}</td>
                        <td className="amount-cell">{grandTotals.amount > 0 ? (grandTotals.tds / grandTotals.amount * 100).toFixed(2) : '0.00'}%</td>
                        <td className="amount-cell">{grandTotals.count}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <Download className="empty-state-icon" />
                  <p className="empty-state-title">No data available</p>
                  <p className="empty-state-description">Upload party ledgers and TDS transactions to generate summary</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Summary by Section */}
        <TabsContent value="section">
          <Card className="data-card" data-testid="summary-section-table">
            <CardHeader className="data-card-header">
              <CardTitle className="data-card-title">Summary by Section ({summaryBySection.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
              ) : summaryBySection.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="tds-table">
                    <thead>
                      <tr>
                        <th>Section (1961)</th>
                        <th>Section (2025)</th>
                        <th className="text-right">Total Amount</th>
                        <th className="text-right">Total TDS</th>
                        <th className="text-right">Effective Rate</th>
                        <th className="text-right">Txn Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryBySection.map((item) => (
                        <tr key={item.section || 'unclassified'} data-testid={`summary-section-row-${item.section}`}>
                          <td><span className="section-badge text-base px-3 py-1">{item.section || 'Unclassified'}</span></td>
                          <td className="text-xs text-slate-600">{item.new_section || '-'}</td>
                          <td className="amount-cell">{formatCurrency(item.total_amount)}</td>
                          <td className="amount-cell text-emerald-600">{formatCurrency(item.total_tds)}</td>
                          <td className="amount-cell">{item.effective_rate?.toFixed(2) || '0.00'}%</td>
                          <td className="amount-cell">{item.transaction_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <Download className="empty-state-icon" />
                  <p className="empty-state-title">No data available</p>
                  <p className="empty-state-description">Upload TDS transactions to generate section-wise summary</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TDSSummary;
